import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Users, FileText, TrendingUp, Award, Search, Filter,
  BarChart3, Hash, Globe, CheckCircle, Clock, AlertTriangle, Loader2,
} from "lucide-react";

// ─── Small bar chart (CSS only) ──────────────────────────────────────────────
function MiniBar({ value, max, color = "bg-primary" }: { value: number; max: number; color?: string }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-muted-foreground w-6 text-right">{value}</span>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
  sub?: string;
  color: string;
}) {
  return (
    <Card>
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold leading-none">{value}</p>
          <p className="text-sm text-muted-foreground mt-1">{label}</p>
          {sub && <p className="text-xs text-muted-foreground/70">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main View ────────────────────────────────────────────────────────────────
export function JournalistAnalyticsView() {
  const { orgId } = useOrgPermissions();

  const [search, setSearch]            = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterBeat, setFilterBeat]     = useState("all");
  const [filterAssoc, setFilterAssoc]   = useState("all");
  const [filterPassport, setPassport]   = useState("all");
  const [sortBy, setSortBy]             = useState<"name" | "submissions" | "status">("submissions");

  // ── Load accreditations with journalist profiles ──────────────────────────
  const { data: accreditations = [], isLoading: loadingAccr } = useQuery({
    queryKey: ["journalist-analytics-accr", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from("journalist_accreditations" as any)
        .select(`
          id, status, accredited_at,
          passport_status, passport_expiry,
          journalist_profiles(
            id, full_name, email, account_type, beats, nationality, headline
          )
        `)
        .eq("org_id", orgId!) as any);
      return (data ?? []) as any[];
    },
  });

  // ── Load submission counts ────────────────────────────────────────────────
  const { data: submissions = [], isLoading: loadingSubs } = useQuery({
    queryKey: ["journalist-analytics-subs", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from("journalist_submissions" as any)
        .select("journalist_id, status, created_at")
        .eq("org_id", orgId!) as any);
      return (data ?? []) as any[];
    },
  });

  // ── Association memberships for these journalists ─────────────────────────
  const { data: memberships = [] } = useQuery({
    queryKey: ["journalist-analytics-memberships", orgId],
    enabled: accreditations.length > 0,
    queryFn: async () => {
      const jpIds = accreditations.map((a: any) => a.journalist_profiles?.id).filter(Boolean);
      if (!jpIds.length) return [];
      const { data } = await (supabase
        .from("association_members" as any)
        .select("journalist_id, membership_number, status, associations(name, acronym)")
        .in("journalist_id", jpIds)
        .eq("status", "active") as any);
      return (data ?? []) as any[];
    },
  });

  // ── Public stats (coverage counter) ─────────────────────────────────────
  const { data: publicStatsArr = [] } = useQuery({
    queryKey: ["journalist-public-stats", orgId],
    enabled: accreditations.length > 0,
    queryFn: async () => {
      const jpIds = accreditations.map((a: any) => a.journalist_profiles?.id).filter(Boolean);
      if (!jpIds.length) return [];
      const { data } = await (supabase
        .from("journalist_public_stats" as any)
        .select("journalist_profile_id, total_submissions, published_articles, accredited_orgs")
        .in("journalist_profile_id", jpIds) as any);
      return (data ?? []) as any[];
    },
  });

  const coverageByJournalist = useMemo(() => {
    const map: Record<string, { total_submissions: number; published_articles: number; accredited_orgs: number }> = {};
    publicStatsArr.forEach((s: any) => {
      map[s.journalist_profile_id] = {
        total_submissions: s.total_submissions ?? 0,
        published_articles: s.published_articles ?? 0,
        accredited_orgs: s.accredited_orgs ?? 0,
      };
    });
    return map;
  }, [publicStatsArr]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const subsByJournalist = useMemo(() => {
    const map: Record<string, { total: number; published: number; pending: number }> = {};
    submissions.forEach((s: any) => {
      if (!map[s.journalist_id]) map[s.journalist_id] = { total: 0, published: 0, pending: 0 };
      map[s.journalist_id].total++;
      if (s.status === "published") map[s.journalist_id].published++;
      if (s.status === "pending")   map[s.journalist_id].pending++;
    });
    return map;
  }, [submissions]);

  const membershipByJournalist = useMemo(() => {
    const map: Record<string, any[]> = {};
    memberships.forEach((m: any) => {
      if (!map[m.journalist_id]) map[m.journalist_id] = [];
      map[m.journalist_id].push(m);
    });
    return map;
  }, [memberships]);

  // Unique beats across all journalists
  const allBeats = useMemo(() => {
    const b = new Set<string>();
    accreditations.forEach((a: any) => {
      (a.journalist_profiles?.beats ?? []).forEach((beat: string) => b.add(beat));
    });
    return [...b].sort();
  }, [accreditations]);

  // Unique associations
  const allAssocs = useMemo(() => {
    const a = new Map<string, string>();
    memberships.forEach((m: any) => {
      if (m.associations?.name) a.set(m.associations.name, m.associations.acronym);
    });
    return [...a.entries()].map(([name, acronym]) => ({ name, acronym }));
  }, [memberships]);

  // ── Enriched rows ─────────────────────────────────────────────────────────
  const rows = useMemo(() => {
    return accreditations.map((a: any) => {
      const jp       = a.journalist_profiles ?? {};
      const subs     = subsByJournalist[jp.id] ?? { total: 0, published: 0, pending: 0 };
      const assocs   = membershipByJournalist[jp.id] ?? [];
      const coverage = coverageByJournalist[jp.id] ?? { total_submissions: 0, published_articles: 0, accredited_orgs: 0 };
      return { ...a, jp, subs, assocs, coverage };
    });
  }, [accreditations, subsByJournalist, membershipByJournalist, coverageByJournalist]);

  const maxSubs = useMemo(() => Math.max(...rows.map(r => r.subs.total), 1), [rows]);

  // ── Filtered + sorted ─────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows
      .filter(r => {
        const matchSearch = !q ||
          (r.jp.full_name ?? "").toLowerCase().includes(q) ||
          (r.jp.email ?? "").toLowerCase().includes(q);
        const matchStatus  = filterStatus  === "all" || r.status === filterStatus;
        const matchBeat    = filterBeat    === "all" || (r.jp.beats ?? []).includes(filterBeat);
        const matchAssoc   = filterAssoc   === "all" || r.assocs.some((m: any) => m.associations?.name === filterAssoc);
        const matchPassport = filterPassport === "all" || (r.passport_status ?? "none") === filterPassport;
        return matchSearch && matchStatus && matchBeat && matchAssoc && matchPassport;
      })
      .sort((a, b) => {
        if (sortBy === "submissions") return b.subs.total - a.subs.total;
        if (sortBy === "name") return (a.jp.full_name ?? "").localeCompare(b.jp.full_name ?? "");
        return (a.status ?? "").localeCompare(b.status ?? "");
      });
  }, [rows, search, filterStatus, filterBeat, filterAssoc, filterPassport, sortBy]);

  // ── Top-line stats ────────────────────────────────────────────────────────
  const totalJournalists = accreditations.length;
  const activeCount      = accreditations.filter((a: any) => a.status === "active").length;
  const totalSubmissions = submissions.length;
  const expiredPassport  = accreditations.filter((a: any) => a.passport_status === "expired").length;

  // Top 5 by submissions
  const topContributors  = [...rows].sort((a, b) => b.subs.total - a.subs.total).slice(0, 5);

  // Beat distribution
  const beatCounts = useMemo(() => {
    const bc: Record<string, number> = {};
    accreditations.forEach((a: any) => {
      (a.journalist_profiles?.beats ?? []).forEach((b: string) => { bc[b] = (bc[b] ?? 0) + 1; });
    });
    return Object.entries(bc).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [accreditations]);

  const isLoading = loadingAccr || loadingSubs;

  return (
    <div className="space-y-6">
      {isLoading ? (
        <div className="flex justify-center py-24">
          <Loader2 className="w-7 h-7 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Users}     label="Total Journalists" value={totalJournalists} color="bg-blue-100 text-blue-600" />
            <StatCard icon={CheckCircle} label="Active"          value={activeCount}      color="bg-emerald-100 text-emerald-600" />
            <StatCard icon={FileText}  label="Total Submissions" value={totalSubmissions}  color="bg-violet-100 text-violet-600" />
            <StatCard icon={AlertTriangle} label="Expired Passports" value={expiredPassport} color="bg-red-100 text-red-600" />
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Top Journalists by Submissions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {topContributors.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No submission data yet.</p>
                ) : topContributors.map(r => (
                  <div key={r.id} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium truncate max-w-[200px]">{r.jp.full_name ?? "Unknown"}</span>
                      <span className="text-muted-foreground text-xs">{r.subs.published} published</span>
                    </div>
                    <MiniBar value={r.subs.total} max={maxSubs} color="bg-primary" />
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Beat Coverage Distribution</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2.5">
                {beatCounts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No beat data yet.</p>
                ) : beatCounts.map(([beat, n]) => (
                  <div key={beat} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>{beat}</span>
                      <span className="text-muted-foreground text-xs">{n} journalist{n !== 1 ? "s" : ""}</span>
                    </div>
                    <MiniBar value={n} max={totalJournalists} color="bg-violet-500" />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input className="pl-9 h-8 text-sm w-52" placeholder="Search journalist..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-8 text-xs w-36">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="invited">Pending</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="revoked">Revoked</SelectItem>
              </SelectContent>
            </Select>

            {allBeats.length > 0 && (
              <Select value={filterBeat} onValueChange={setFilterBeat}>
                <SelectTrigger className="h-8 text-xs w-36">
                  <SelectValue placeholder="Beat" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Beats</SelectItem>
                  {allBeats.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            )}

            {allAssocs.length > 0 && (
              <Select value={filterAssoc} onValueChange={setFilterAssoc}>
                <SelectTrigger className="h-8 text-xs w-44">
                  <SelectValue placeholder="Association" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Associations</SelectItem>
                  {allAssocs.map(a => <SelectItem key={a.name} value={a.name}>{a.acronym} – {a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}

            <Select value={filterPassport} onValueChange={setPassport}>
              <SelectTrigger className="h-8 text-xs w-36">
                <SelectValue placeholder="Passport" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Passports</SelectItem>
                <SelectItem value="valid">Valid</SelectItem>
                <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="none">No Passport</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
              <SelectTrigger className="h-8 text-xs w-36">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="submissions">Most Submissions</SelectItem>
                <SelectItem value="name">Name A–Z</SelectItem>
                <SelectItem value="status">Status</SelectItem>
              </SelectContent>
            </Select>

            <span className="ml-auto text-xs text-muted-foreground">{filtered.length} journalist{filtered.length !== 1 ? "s" : ""}</span>
          </div>

          {/* Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Journalist</TableHead>
                  <TableHead>Beats</TableHead>
                  <TableHead>Association</TableHead>
                  <TableHead>Submissions</TableHead>
                  <TableHead>Published</TableHead>
                  <TableHead>Coverage</TableHead>
                  <TableHead>Passport</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                      No journalists match the current filters.
                    </TableCell>
                  </TableRow>
                ) : filtered.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{r.jp.full_name ?? "Unknown"}</p>
                        <p className="text-xs text-muted-foreground">{r.jp.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(r.jp.beats ?? []).slice(0, 2).map((b: string) => (
                          <Badge key={b} variant="secondary" className="text-[10px] px-1.5 py-0.5">{b}</Badge>
                        ))}
                        {(r.jp.beats ?? []).length > 2 && (
                          <span className="text-xs text-muted-foreground">+{r.jp.beats.length - 2}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {r.assocs.length === 0 ? (
                        <span className="text-xs text-muted-foreground italic">Independent</span>
                      ) : r.assocs.map((m: any) => (
                        <div key={m.membership_number} className="flex items-center gap-1 text-xs">
                          <Hash className="w-3 h-3 text-muted-foreground" />
                          <span>{m.membership_number}</span>
                          <span className="text-muted-foreground">· {m.associations?.acronym}</span>
                        </div>
                      ))}
                    </TableCell>
                    <TableCell>
                      <span className="font-semibold">{r.subs.total}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-emerald-700 font-medium">{r.subs.published}</span>
                      {r.subs.pending > 0 && (
                        <span className="text-amber-600 text-xs ml-1">({r.subs.pending} pending)</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5 text-xs">
                        <span className="font-semibold">{r.coverage.published_articles}</span>
                        <span className="text-muted-foreground">articles</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const ps = r.passport_status ?? "none";
                        const colors: Record<string, string> = {
                          valid:         "bg-emerald-100 text-emerald-700",
                          expiring_soon: "bg-amber-100 text-amber-700",
                          expired:       "bg-red-100 text-red-700",
                          none:          "bg-gray-100 text-gray-500",
                        };
                        return (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${colors[ps] ?? colors.none}`}>
                            {ps === "expired" && <AlertTriangle className="w-3 h-3" />}
                            {ps === "expiring_soon" && <Clock className="w-3 h-3" />}
                            {ps.replace("_", " ")}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        r.status === "active" ? "bg-emerald-100 text-emerald-700" :
                        r.status === "invited" ? "bg-blue-100 text-blue-700" :
                        r.status === "suspended" ? "bg-amber-100 text-amber-700" :
                        "bg-gray-100 text-gray-500"
                      }`}>
                        {r.status}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}
